/**
 * SOC-09 — Centralized UTM + referrer attribution for PostHog and Stripe.
 *
 * Captures first-touch (localStorage, never overwritten) and last-touch
 * (overwritten on each visit with UTM params) attribution data.
 *
 * Must be called AFTER posthog.init() so super properties attach to the
 * first autocaptured pageview in the session. The SDK instance is passed in
 * by src/lib/posthog.ts; this module never imports posthog-js at runtime so
 * it can stay in the entry chunk without dragging the SDK along.
 *
 * Expected UTM sources:
 *   postcard / direct_mail   — QR scans from postcards
 *   facebook / social        — paid or organic social
 *   google / cpc             — Google Ads
 *   google / organic         — organic search (referrer-detected)
 *   direct / none            — typed URL, bookmarked, dark social
 */

import type { PostHog } from "posthog-js";
import { safeCheckoutCampaignTag } from "../../supabase/functions/_shared/checkout-campaign-attribution";
import { sanitizeTelemetryUrl } from "./telemetryPrivacy";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Attribution {
  source: string;
  medium: string;
  campaign: string;
  content: string;
  term: string;
  /** OpenAI Ads click reference appended to destination URLs. */
  openai_click_ref: string;
  landing_page: string;
  timestamp: string;
}

export interface AttributionData {
  first_touch: Attribution;
  last_touch: Attribution;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

const LS_PREFIX_FIRST = "fp_first_touch_";
const LS_PREFIX_LAST = "fp_last_touch_";
const SS_PREFIX = "fp_"; // existing sessionStorage keys for Stripe compat
const OPENAI_CLICK_REF_PARAM = "oppref";

// Referrer → source/medium mapping
const REFERRER_MAP: Array<{ pattern: string; source: string; medium: string }> = [
  { pattern: "google", source: "google", medium: "organic" },
  { pattern: "bing", source: "bing", medium: "organic" },
  { pattern: "duckduckgo", source: "duckduckgo", medium: "organic" },
  { pattern: "yahoo", source: "yahoo", medium: "organic" },
  { pattern: "facebook", source: "facebook", medium: "social_organic" },
  { pattern: "instagram", source: "instagram", medium: "social_organic" },
  { pattern: "linkedin", source: "linkedin", medium: "social_organic" },
  { pattern: "nextdoor", source: "nextdoor", medium: "social_organic" },
  { pattern: "twitter", source: "twitter", medium: "social_organic" },
  { pattern: "t.co", source: "twitter", medium: "social_organic" },
  { pattern: "reddit", source: "reddit", medium: "social_organic" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function safeGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage full or unavailable (Safari private mode)
  }
}

function parseReferrer(referrer: string): { source: string; medium: string } {
  if (!referrer) return { source: "direct", medium: "none" };

  const lower = referrer.toLowerCase();
  for (const entry of REFERRER_MAP) {
    if (lower.includes(entry.pattern)) {
      return { source: entry.source, medium: entry.medium };
    }
  }

  // Unknown referrer — extract hostname
  try {
    const hostname = new URL(referrer).hostname.replace(/^www\./, "");
    return { source: hostname, medium: "referral" };
  } catch {
    return { source: "unknown", medium: "referral" };
  }
}

function readAttribution(prefix: string): Attribution | null {
  const source = safeGet(localStorage, `${prefix}source`);
  if (!source) return null;
  return {
    source: sanitizeTelemetryUrl(source),
    medium: sanitizeTelemetryUrl(safeGet(localStorage, `${prefix}medium`) || "none"),
    campaign: sanitizeTelemetryUrl(safeGet(localStorage, `${prefix}campaign`) || ""),
    content: safeCheckoutCampaignTag(safeGet(localStorage, `${prefix}content`)) ?? "",
    term: safeCheckoutCampaignTag(safeGet(localStorage, `${prefix}term`)) ?? "",
    openai_click_ref: sanitizeTelemetryUrl(safeGet(localStorage, `${prefix}openai_click_ref`) || ""),
    landing_page: sanitizeTelemetryUrl(safeGet(localStorage, `${prefix}landing_page`) || ""),
    timestamp: safeGet(localStorage, `${prefix}timestamp`) || "",
  };
}

function writeAttribution(prefix: string, data: Attribution): void {
  safeSet(localStorage, `${prefix}source`, data.source);
  safeSet(localStorage, `${prefix}medium`, data.medium);
  safeSet(localStorage, `${prefix}campaign`, data.campaign);
  safeSet(localStorage, `${prefix}content`, data.content);
  safeSet(localStorage, `${prefix}term`, data.term);
  safeSet(localStorage, `${prefix}openai_click_ref`, data.openai_click_ref);
  safeSet(localStorage, `${prefix}landing_page`, data.landing_page);
  safeSet(localStorage, `${prefix}timestamp`, data.timestamp);
}

// ---------------------------------------------------------------------------
// Core capture logic
// ---------------------------------------------------------------------------

/**
 * Read UTM params from URL, or fall back to referrer detection.
 * Returns an Attribution object representing the current visit.
 */
function resolveCurrentVisit(): Attribution {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");

  let source: string;
  let medium: string;

  if (utmSource) {
    // Explicit UTM params — use them
    source = utmSource;
    medium = params.get("utm_medium") || "none";
  } else {
    // No UTM — detect from referrer
    const detected = parseReferrer(document.referrer);
    source = detected.source;
    medium = detected.medium;
  }

  return {
    source: sanitizeTelemetryUrl(source),
    medium: sanitizeTelemetryUrl(medium),
    campaign: sanitizeTelemetryUrl(params.get("utm_campaign") || ""),
    content: safeCheckoutCampaignTag(params.get("utm_content")) ?? "",
    term: safeCheckoutCampaignTag(params.get("utm_term")) ?? "",
    openai_click_ref: sanitizeTelemetryUrl(params.get(OPENAI_CLICK_REF_PARAM) || ""),
    landing_page: sanitizeTelemetryUrl(window.location.pathname),
    timestamp: new Date().toISOString(),
  };
}

/**
 * Main entry point. Call once after posthog.init() with the initialized SDK.
 *
 * 1. Resolves current visit attribution (UTM or referrer)
 * 2. Writes first-touch (only if not already set) and last-touch
 * 3. Registers PostHog super properties (attach to every event)
 * 4. Sets PostHog person properties ($initial_ for first touch)
 * 5. Writes sessionStorage for Stripe checkout compatibility
 */
export function captureAttribution(posthog: PostHog): void {
  const current = resolveCurrentVisit();

  // --- First touch: only set once (persists across sessions) ---
  const existingFirst = readAttribution(LS_PREFIX_FIRST);
  if (!existingFirst) {
    writeAttribution(LS_PREFIX_FIRST, current);
  }

  // --- Last touch: always overwrite (but only when UTM params are present,
  //     so organic re-visits don't erase a paid last-touch) ---
  const params = new URLSearchParams(window.location.search);
  if (params.get("utm_source") || params.get(OPENAI_CLICK_REF_PARAM)) {
    writeAttribution(LS_PREFIX_LAST, current);
  } else if (!readAttribution(LS_PREFIX_LAST)) {
    // No last-touch exists yet — set from current (referrer/direct)
    writeAttribution(LS_PREFIX_LAST, current);
  }

  // --- SessionStorage for backward compat (Stripe checkout reads fp_utm_*) ---
  if (params.get("utm_source")) {
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      const safeValue = key === "utm_content" || key === "utm_term"
        ? safeCheckoutCampaignTag(value)
        : value;
      if (safeValue) safeSet(sessionStorage, `${SS_PREFIX}${key}`, safeValue);
    }
  }
  const openaiClickRef = params.get(OPENAI_CLICK_REF_PARAM);
  if (openaiClickRef) {
    safeSet(sessionStorage, `${SS_PREFIX}openai_click_ref`, openaiClickRef);
  }

  // --- PostHog super properties (attach to every event this session) ---
  if (posthog.__loaded) {
    const firstTouch = readAttribution(LS_PREFIX_FIRST) || current;
    const lastTouch = readAttribution(LS_PREFIX_LAST) || current;

    posthog.register({
      utm_source: lastTouch.source,
      utm_medium: lastTouch.medium,
      utm_campaign: lastTouch.campaign,
      utm_content: lastTouch.content,
      utm_term: lastTouch.term,
      openai_click_ref: lastTouch.openai_click_ref || null,
      first_touch_source: firstTouch.source,
      first_touch_medium: firstTouch.medium,
      first_touch_campaign: firstTouch.campaign,
      first_touch_content: firstTouch.content,
      first_touch_term: firstTouch.term,
    });

    // Person properties: $initial_ prefix for first-touch (PostHog convention:
    // $initial_ properties are set-once on the person, not overwritten).
    posthog.people.set_once({
      $initial_utm_source: firstTouch.source,
      $initial_utm_medium: firstTouch.medium,
      $initial_utm_campaign: firstTouch.campaign,
      $initial_utm_content: firstTouch.content,
      $initial_utm_term: firstTouch.term,
      $initial_landing_page: firstTouch.landing_page,
      first_touch_timestamp: firstTouch.timestamp,
    });

    // Last-touch person properties (always overwritten)
    posthog.people.set({
      utm_source: lastTouch.source,
      utm_medium: lastTouch.medium,
      utm_campaign: lastTouch.campaign,
      utm_content: lastTouch.content,
      utm_term: lastTouch.term,
      openai_click_ref: lastTouch.openai_click_ref || null,
    });
  }
}

// ---------------------------------------------------------------------------
// Public getter — used by Stripe checkout and any component that needs
// attribution data without depending on PostHog.
// ---------------------------------------------------------------------------

/**
 * Returns the current first-touch and last-touch attribution data.
 * Falls back to resolving the current visit if nothing is stored yet.
 */
export function getAttribution(): AttributionData {
  const current = resolveCurrentVisit();
  return {
    first_touch: readAttribution(LS_PREFIX_FIRST) || current,
    last_touch: readAttribution(LS_PREFIX_LAST) || current,
  };
}
